/**
 * Marker — mono uppercase label with a leading 8 px square dot.
 * Used for state markers, etc. (A tyre set's season is a `SezonaBadge`, a Badge with a snowflake / sun.)
 *
 * Tones map to the semantic palette tokens. Custom hues (e.g. plum/winter from
 * the designer's seasonal palette) are not first-class — overriding via
 * `className` is acceptable for one-off cases.
 *
 * Source: .agents/design/project/artboard-radical.jsx:220-239
 */

export type MarkerTone = 'accent' | 'ok' | 'warn' | 'neutral' | 'danger';

const TONE_CLASSES: Record<MarkerTone, { text: string; dot: string }> = {
  accent: { text: 'text-accent-text', dot: 'bg-accent' },
  ok: { text: 'text-ok', dot: 'bg-ok' },
  warn: { text: 'text-warn', dot: 'bg-warn' },
  neutral: { text: 'text-ink-3', dot: 'bg-ink-3' },
  // Overdue / alert — text uses the AA `--red-text`, dot the solid `--red`.
  danger: { text: 'text-red-text', dot: 'bg-red' },
};

interface MarkerProps {
  children: React.ReactNode;
  tone?: MarkerTone;
  className?: string;
}

export function Marker({ children, tone = 'neutral', className = '' }: MarkerProps) {
  const t = TONE_CLASSES[tone];
  return (
    <span
      className={
        'inline-flex items-center gap-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] ' +
        t.text +
        ' ' +
        className
      }
    >
      <span aria-hidden className={'inline-block size-2 ' + t.dot} />
      {children}
    </span>
  );
}
